import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useDashboardStore from '../store/useDashboardStore';
import { getSseToken } from '../utils/helpers';

/**
 * SSE-Consumer: Verbindet zu /api/events wenn experimentalCaching aktiv.
 * Invalidiert TanStack Query Caches bei Server-Events.
 *
 * Auth: Da EventSource keine Custom-Headers unterstützt, wird das JWT-Token
 * als Query-Parameter übergeben. Das Backend akzeptiert ?token= für den
 * /api/events Endpoint.
 */
export function useSSE() {
  const experimentalCaching = useDashboardStore(s => s.experimentalCaching);
  const queryClient = useQueryClient();
  const esRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const backoffRef = useRef(1000);

  useEffect(() => {
    if (!experimentalCaching) return;

    let abgebrochen = false;
    async function connect() {
      // Kurzlebiges SSE-Token holen (statt das 7-Tage-Sitzungstoken in die URL zu legen).
      const token = await getSseToken();
      if (!token || abgebrochen) return;

      const es = new EventSource(`/api/events?token=${encodeURIComponent(token)}`);
      esRef.current = es;

      es.addEventListener('cache-invalidated', (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.instanceId) {
            queryClient.invalidateQueries({ queryKey: [data.instanceId] });
          }
          queryClient.invalidateQueries({ queryKey: ['global'] });
        } catch { /* ignore parse errors */ }
      });

      es.addEventListener('connected', () => {
        backoffRef.current = 1000;
      });

      es.onerror = () => {
        es.close();
        esRef.current = null;
        reconnectTimerRef.current = setTimeout(connect, backoffRef.current);
        backoffRef.current = Math.min(backoffRef.current * 2, 30000);
      };
    }

    connect();

    return () => {
      abgebrochen = true;
      if (esRef.current) esRef.current.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      backoffRef.current = 1000;
    };
  }, [experimentalCaching, queryClient]);
}
